import { DateTime } from 'luxon';
import axios from 'smg/axios';
import Batch from 'smg/batch';
import * as models from 'smg/customer-api/models';
import * as html from 'smg/html';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { AccountDetailsInit, PageLoader, RequireSession } from '../../account';
import { Relref } from '../../hugo';

declare interface myWindow extends Window {
    readonly apiRoot: string;
}
declare const window: myWindow;

void main();

async function main() {
    if (await RequireSession()) {
        return;
    }

    await AccountDetailsInit();
    const b = new Batch();

    const allPaymentsCount = b.get('/order-payments', {
        params: {
            'colStats': 'id',
        },
    }).then(resp => models.ColumnStatsResponse.fromJSON(resp.data).count);

    void b.exec();

    const $tableContainer = document.querySelector('#table-container') as HTMLElement;
    const $cardContainer = document.querySelector('#card-container') as HTMLElement;
    const $noResults = document.querySelector('.no-results') as HTMLElement;
    const $controlsContainer = document.querySelector('.controls-container') as HTMLElement;

    const $table = createPaymentHistoryTable();
    $tableContainer.appendChild($table);
    const $tBody = $table.querySelector('tbody') as HTMLElement;
    let $tableRows: HTMLElement[] = [];

    const $qtyResults = document.querySelector('.controls-container')?.querySelector('.results-qty') as HTMLElement;

    const pl: PageLoader<models.OrderPayment> = new PageLoader<models.OrderPayment>({
        clearResults: true,
        parent: $cardContainer,
        beforeLoad: () => {
            $tableRows = [];
            $tableContainer.classList.add('opacity-50');
        },
        get: () => axios.get('/order-payments', {
            params: {
                'sort': 'created desc',
                'max': 5,
                'data': 'total,created,paymentOption,orderPayments,invoiceNumber',
                'p': pl.currentPage(),
                'q': pl.search(),
            },
        }).then(resp => {
            const data = models.OrderPaymentsHyejooResponse.fromJSON(resp.data);
            // Filter out invalid order payments (no created date means invalid record)
            const validPayments = data.orderPayments.filter(op => op.created.isValid);
            if (validPayments.length === 0) {
                $noResults.classList.remove('d-none');
                $tableContainer.classList.add('d-none');
                $tableContainer.classList.remove('d-sm-block');
                $cardContainer.classList.add('d-none');
                $controlsContainer.classList.add('d-none');
            } else {
                $noResults.classList.add('d-none');
                $tableContainer.classList.remove('d-none');
                $tableContainer.classList.add('d-sm-block');
                $cardContainer.classList.remove('d-none');
                $controlsContainer.classList.remove('d-none');
            }
            return { rows: validPayments, nextPage: data.nextPage };
        }),
        draw: orderPayment => {
            const $card = createPaymentHistoryCard(orderPayment);
            $tableRows.push(createPaymentHistoryTableRow(orderPayment));
            return $card;
        },
        afterLoad: async () => {
            const $currTableRows = Array.from($tBody.querySelectorAll('tr'));
            if ($currTableRows.length && $tableRows.length) {
                for (let i = 0; i < $tableRows.length; i++) {
                    if ($currTableRows[i]) {
                        $currTableRows[i].replaceWith($tableRows[i]);
                    } else {
                        $tBody.appendChild($tableRows[i]);
                    }
                    if (i === $tableRows.length - 1) {
                        const $remainingRows = $currTableRows.slice(i + 1);
                        for (const $remainingRow of $remainingRows) {
                            $remainingRow.remove();
                        }
                    }
                }
            } else {
                for (const $row of $tableRows) {
                    $tBody.appendChild($row);
                }
            }
            $tableContainer.classList.remove('opacity-50');

            const results = Math.min((pl.currentPage() + 1) * 5, await allPaymentsCount);
            $qtyResults.textContent = `Showing ${String(results)} of ${await allPaymentsCount} results`;
        },
    });

    pl.activate();
    pl.load();
}

function createPaymentHistoryTable(): HTMLElement {
    const $table = html.FromString(`<table class="table mw-100 overflow-hidden">
                                    <thead>
                                        <tr>
                                            <th style="background-color: #f4f6fc;">Date</th>
                                            <th class="text-end" style="background-color: #f4f6fc;">Order no.</th>
                                            <th class="text-end" style="background-color: #f4f6fc;">Amount</th>
                                            <th class="text-end" style="background-color: #f4f6fc;">Invoice</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>2025-05-26</td>
                                            <td>#12345</td>
                                            <td>$250.00</td>
                                            <td><a href="invoice12345.pdf">Download</a></td>
                                        </tr>
                                    </tbody>
                                </table>`).firstElementChild as HTMLTableElement;

    const tbody = $table.querySelector('tbody') as HTMLElement;
    tbody.innerHTML = '';

    return $table;
}

function createPaymentHistoryTableRow(orderPayment: models.OrderPayment): HTMLElement {
    const orders: models.Order[] = [];
    if (orderPayment.orderPayments.length > 0) {
        for (const oPayment of orderPayment.orderPayments) {
            if (oPayment.order) {
                orders.push(oPayment.order);
            }
        }
    }

    const $orderLinks: HTMLAnchorElement[] = [];
    const $invoiceLinks: HTMLAnchorElement[] = [];
    for (const order of orders) {
        if (order.invoiceNumber) {
            const $ol = html.FromString(`<a class="order-link text-end" href="${Relref('/account/order')}?id=${order.id}">#${String(order.invoiceNumber)}</a>`).firstElementChild as HTMLAnchorElement;
            $orderLinks.push($ol as HTMLAnchorElement);
        }
        if (order.id) {
            const $il = html.FromString(`
                <a class="invoice-link d-flex align-items-center justify-content-center btn btn-link dl-invoice-btn py-0 me-sm-n2"
                    href="${window.apiRoot}/orders/${order.id}/invoice">
                    <span class="material-symbols-rounded pe-md-1">download</span>
                    <span class="d-none d-md-inline">Download</span>
                </a>
            `).firstElementChild as HTMLAnchorElement;
            $invoiceLinks.push($il as HTMLAnchorElement);
        }
    }

    const $row = html.FromString(`<tr>
                                    <td style="vertical-align: middle;">${orderPayment.created.isValid ? orderPayment.created.toLocaleString(DateTime.DATETIME_MED) : ''}</td>
                                    <td style="vertical-align: middle;"><div class="order-links-container d-flex flex-column justify-content-center"></div></td>
                                    <td style="vertical-align: middle;" class="text-end">${DollarFormat(orderPayment.total)}</td>
                                    <td style="vertical-align: middle;" class="text-end">
                                        <div class="d-flex flex-column align-items-end justify-content-center invoice-links-container gap-4"></div>
                                    </td>
                                </tr>`).firstElementChild as HTMLTableRowElement;

    const $orderLinksContainer = $row.querySelector('.order-links-container') as HTMLElement;
    const $invoiceLinksContainer = $row.querySelector('.invoice-links-container') as HTMLElement;

    for (const $ol of $orderLinks) {
        $orderLinksContainer.appendChild($ol);
    }
    for (const $il of $invoiceLinks) {
        $invoiceLinksContainer.appendChild($il);
    }

    return $row;
}

function createPaymentHistoryCard(orderPayment: models.OrderPayment): HTMLElement {
    const $card = template.Clone('#payment-history-card-template').firstElementChild as HTMLElement;

    const $total = $card.querySelector('.total') as HTMLElement;
    const $orderDate = $card.querySelector('.date') as HTMLElement;

    if (orderPayment.orderPayments.length > 0) {
        if (orderPayment.orderPayments.length === 1) {
            const $orderLinksContainer = $card.querySelector('.order-links-container') as HTMLElement;
            const $invoiceLinksContainer = $card.querySelector('.invoice-links-container') as HTMLElement;

            const $orderLink = template.Clone('#order-link-template').firstElementChild as HTMLAnchorElement;
            const $invoiceLink = template.Clone('#invoice-download-template').firstElementChild as HTMLAnchorElement;

            $orderLink.textContent = `Order #${String(orderPayment.orderPayments[0].order?.invoiceNumber)}`;
            $orderLink.href = `${Relref('/account/order')}?id=${orderPayment.orderPayments[0].order?.id || ''}`;
            $invoiceLink.href = `${window.apiRoot}/orders/${orderPayment.orderPayments[0].order?.id ?? ''}/invoice`;

            $orderLinksContainer.appendChild($orderLink);
            $invoiceLinksContainer.appendChild($invoiceLink);
            $total.textContent = DollarFormat(orderPayment.total);
        } else {
            const $multipleOrdersPaymentContainer = $card.querySelector('.multiple-orders-payment-container') as HTMLElement;
            $multipleOrdersPaymentContainer.classList.remove('d-none');
            for (const oPayment of orderPayment.orderPayments) {
                if (oPayment.order) {
                    const $multipleOrdersLink = template.Clone('#multiple-orders-payment-template').firstElementChild as HTMLElement;
                    const $total = $multipleOrdersLink.querySelector('.total') as HTMLElement;
                    const $orderLink = $multipleOrdersLink.querySelector('.order') as HTMLAnchorElement;
                    const $invoiceLink = $multipleOrdersLink.querySelector('.invoice') as HTMLAnchorElement;

                    $total.textContent = DollarFormat(oPayment.total);
                    $orderLink.textContent = `Order #${String(oPayment.order.invoiceNumber)}`;
                    $orderLink.href = `${Relref('/account/order')}?id=${oPayment.order.id}`;
                    $invoiceLink.href = `${window.apiRoot}/orders/${oPayment.order.id}/invoice`;

                    $multipleOrdersPaymentContainer.appendChild($multipleOrdersLink);
                }
            }
        }
    }

    $orderDate.textContent = orderPayment.created.isValid ? orderPayment.created.toLocaleString(DateTime.DATETIME_MED) : '';

    return $card;
}
